import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocale } from '../context/LocaleContext';
import { monthGrid } from './DateRangePicker';
import Icon from './Icon';
import pick from './DateRangePicker.module.css';
import s from './RegionInputs.module.css';

// The boxes a teacher types a date, a time or an amount into, in the studio's
// Region (#526).
//
// MMS's boxes follow the Region: in Germany an expense's Date box holds
// `30.09.2026` and the Amount box `16,95`. `<input type="date">`, `type="time"`
// and `type="number"` follow the browser's locale instead, so a German studio
// on an American laptop could not type `16,95`. Each box here shows what it
// holds as the studio's screens write it, and reads what is typed by the
// core's rules (`parse_typed_date`, `parse_time`, `typed_decimal`). What a box
// holds, and hands its caller, is the stored spelling: `2026-09-30`, `16:00`,
// `16.95`.
//
// Each takes the props the element it replaces took and calls `onChange` with
// an event-shaped `{ target: { value } }`, so a call site changes its tag and
// nothing else.

const asEvent = (value) => ({ target: { value } });

// ── A date ─────────────────────────────────────────────────────────────────
//
// A text box showing the studio's short date (`30.09.2026`, `9/30/2026`) and
// a button opening a month to pick from, which begins on the Region's first
// weekday and names its days in the Region's language — the date range
// picker's month, one end instead of two.
//
// What is typed is read as it is typed: a whole date, in the Region's order
// or as `2026-09-30`, reaches the caller at once; anything else waits, and on
// leaving the box it is put back to the date the box holds. A cleared box is
// a cleared date.
export function DateInput({
  value, onChange, min, max, className, placeholder, disabled, id, ...rest
}) {
  const { formatDate, parseDate, dateFormat, weekStartsOn, weekdayNames, formatMonth } = useLocale();
  const [text, setText] = useState(() => formatDate(value || ''));
  const [open, setOpen] = useState(false);
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText(formatDate(value || ''));
  }, [value, formatDate]);

  const emit = (iso) => { if (iso !== (value || '')) onChange?.(asEvent(iso)); };

  const typed = (e) => {
    const next = e.target.value;
    setText(next);
    if (!next.trim()) emit('');
    else {
      const iso = parseDate(next);
      if (iso) emit(iso);
    }
  };

  const leave = () => {
    focused.current = false;
    if (!text.trim()) return;
    const iso = parseDate(text);
    setText(formatDate(iso || value || ''));
    if (iso) emit(iso);
  };

  return (
    <span className={s.wrap}>
      <input
        {...rest}
        id={id}
        type="text"
        data-date-input="true"
        autoComplete="off"
        className={className}
        value={text}
        placeholder={placeholder ?? dateFormat}
        disabled={disabled}
        onFocus={() => { focused.current = true; }}
        onChange={typed}
        onBlur={leave}
      />
      <button
        type="button"
        className={s.trigger}
        aria-label="Choose a date"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="calendar" size={16} />
      </button>
      {open ? (
        <MonthPicker
          value={value || ''}
          min={min}
          max={max}
          weekStartsOn={weekStartsOn}
          weekdayNames={weekdayNames}
          formatMonth={formatMonth}
          onPick={(iso) => { setOpen(false); setText(formatDate(iso)); emit(iso); }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </span>
  );
}

function MonthPicker({ value, min, max, weekStartsOn, weekdayNames, formatMonth, onPick, onClose }) {
  const [view, setView] = useState(() => {
    const [y, m] = String(value || '').split('-').map(Number);
    if (y) return { year: y, month: m - 1 };
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const grid = useMemo(() => monthGrid(view.year, view.month, weekStartsOn), [view, weekStartsOn]);
  const dayNames = weekdayNames.map((_, i) => weekdayNames[(i + weekStartsOn) % 7]);
  const step = (delta) => setView((v) => {
    const d = new Date(Date.UTC(v.year, v.month + delta, 1));
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
  });

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className={pick.scrim} onClick={onClose} />
      <div className={`${pick.pop} ${s.pop}`} role="dialog" aria-label="Choose a date">
        <div className={pick.head}>
          <span className={pick.title}>{formatMonth(`${view.year}-${String(view.month + 1).padStart(2, '0')}`)}</span>
          <button type="button" className={pick.nav} onClick={() => step(-1)} aria-label="Previous month">‹</button>
          <button type="button" className={pick.nav} onClick={() => step(1)} aria-label="Next month">›</button>
        </div>
        <div className={pick.grid}>
          {dayNames.map((d) => <span key={d} className={pick.dow}>{d}</span>)}
          {grid.map((cell) => (
            <button
              type="button"
              key={cell.value}
              className={cell.value === value ? pick.dayEdge : cell.outside ? pick.dayOutside : pick.day}
              disabled={Boolean((min && cell.value < min) || (max && cell.value > max))}
              onClick={() => onPick(cell.value)}
            >
              {cell.day}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ── A time ─────────────────────────────────────────────────────────────────
//
// A text box showing the stored `16:00` on the Region's clock (`4:00 PM`,
// `16:00`), offering the day's quarter hours as suggestions in the same
// writing, and reading what is typed on either clock. As with a date: a
// reading that makes sense reaches the caller at once, and leaving the box
// puts back what it holds.
const QUARTERS = Array.from({ length: 96 }, (_, i) =>
  `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`);

export function TimeInput({ value, onChange, className, disabled, id, list: _list, ...rest }) {
  const { formatTime, parseTime } = useLocale();
  const [text, setText] = useState(() => (value ? formatTime(value) : ''));
  const focused = useRef(false);
  const listId = useMemo(() => `times-${Math.random().toString(36).slice(2)}`, []);

  useEffect(() => {
    if (!focused.current) setText(value ? formatTime(value) : '');
  }, [value, formatTime]);

  const emit = (hhmm) => { if (hhmm !== (value || '')) onChange?.(asEvent(hhmm)); };

  return (
    <>
      <input
        {...rest}
        id={id}
        type="text"
        data-time-input="true"
        autoComplete="off"
        className={className}
        value={text}
        disabled={disabled}
        list={listId}
        onFocus={() => { focused.current = true; }}
        onChange={(e) => {
          setText(e.target.value);
          const hhmm = parseTime(e.target.value);
          if (hhmm) emit(hhmm);
          else if (!e.target.value.trim()) emit('');
        }}
        onBlur={() => {
          focused.current = false;
          const hhmm = parseTime(text);
          setText(hhmm ? formatTime(hhmm) : value ? formatTime(value) : '');
          if (hhmm) emit(hhmm);
        }}
      />
      <datalist id={listId}>
        {QUARTERS.map((q) => <option key={q} value={formatTime(q)} />)}
      </datalist>
    </>
  );
}

// ── A number with a fraction ─────────────────────────────────────────────────
//
// An amount, a distance, a count of credits: the box shows the held figure
// with the Region's decimal separator (`16,95`) and hands back what is typed
// with a point (`16.95`): in a comma Region the comma is the point and a typed
// `.` a digit group, so `16.95` is 1695 there as it is in MMS (#551); anywhere
// else the last `.` or `,` typed (`typed_decimal` in core/src/money.rs has the
// argument). A caller's own clamp and checks run on the held figure, and the
// box redraws from it, so a swallowed third decimal stays swallowed. `min`,
// `max` and `step` mean nothing to a text box; the callers' checks refuse.
export function DecimalInput({
  value, onChange, className, min: _min, max: _max, step: _step, placeholder, ...rest
}) {
  const { shownDecimal, typedDecimal, decimalSeparator } = useLocale();
  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      className={className}
      value={shownDecimal(value ?? '')}
      placeholder={placeholder == null ? placeholder : String(placeholder).replace('.', decimalSeparator)}
      onChange={(e) => onChange?.(asEvent(typedDecimal(e.target.value)))}
    />
  );
}

// Each puts the `id` it is handed on its input, so a `Field` labels it.
DateInput.labelable = true;
TimeInput.labelable = true;
DecimalInput.labelable = true;
